const PREFIX = "f_";

// Keys that are safe to evict when storage is full (ordered by priority to evict)
const EVICTABLE_PREFIXES = ["mp_", "fl_", "cip_", "wh_", "ci_"];
// Maximum storage budget in bytes (5MB is typical localStorage limit)
const MAX_STORAGE_BYTES = 4 * 1024 * 1024;
// Max age for workout history and check-in entries (180 days)
const MAX_HISTORY_AGE_MS = 180 * 24 * 60 * 60 * 1000;

function evictOldData() {
  try {
    const keysWithSize = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(PREFIX)) continue;
      const innerKey = key.slice(PREFIX.length);
      for (const prefix of EVICTABLE_PREFIXES) {
        if (innerKey.startsWith(prefix)) {
          keysWithSize.push({ key, size: (localStorage.getItem(key) || "").length });
          break;
        }
      }
    }
    // Sort by size descending (evict biggest first)
    keysWithSize.sort((a, b) => b.size - a.size);
    // Evict up to 5 items to free space
    const toRemove = keysWithSize.slice(0, 5);
    toRemove.forEach(({ key }) => localStorage.removeItem(key));
    return toRemove.length > 0;
  } catch {
    return false;
  }
}

const storage = {
  get(key, defaultValue) {
    try {
      const item = localStorage.getItem(PREFIX + key);
      return item !== null ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(PREFIX + key, JSON.stringify(value));
    } catch (e) {
      // Storage full — evict old data and retry once
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        evictOldData();
        this.evictOldEntries();
        try {
          localStorage.setItem(PREFIX + key, JSON.stringify(value));
        } catch {
          // Still full after cleanup — nothing more we can do
        }
      }
    }
  },
  remove(key) {
    try {
      localStorage.removeItem(PREFIX + key);
    } catch {
      // ignore
    }
  },
  clear() {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(PREFIX)) keysToRemove.push(key);
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch {
      // ignore
    }
  },
  /**
   * Evict old workout history and check-in entries to free space.
   * Removes entries older than MAX_HISTORY_AGE_MS, starting with oldest.
   */
  evictOldEntries() {
    try {
      const now = Date.now();
      const keysToCheck = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith("f_wh_") || key.startsWith("f_ci_"))) {
          const ts = Number(key.replace(/^f_(wh|ci)_/, ""));
          if (!isNaN(ts)) keysToCheck.push({ key, ts });
        }
      }
      // Sort oldest first
      keysToCheck.sort((a, b) => a.ts - b.ts);
      for (const entry of keysToCheck) {
        if (now - entry.ts > MAX_HISTORY_AGE_MS) {
          localStorage.removeItem(entry.key);
        }
      }
    } catch {
      // ignore
    }
  },
  /**
   * Get approximate storage usage in bytes for forge-prefixed keys.
   * @returns {number} Approximate bytes used
   */
  getUsageBytes() {
    try {
      let total = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(PREFIX)) {
          const val = localStorage.getItem(key);
          total += (key.length + (val ? val.length : 0)) * 2; // UTF-16
        }
      }
      return total;
    } catch {
      return 0;
    }
  },
};

export default storage;
